import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Link, useParams } from "react-router-dom";
import AISupport from "./AISupport";
import {
  IoLogInOutline,
  IoPersonAddOutline,
  IoSearchOutline,
} from "react-icons/io5";
import { marketingI18n } from "../content/homeMarketing";
import useScrollAnimate from "../hooks/useScrollAnimate";
import DownloadAppSection from "./marketing/DownloadAppSection";
import HomeQuickLinks from "./marketing/HomeQuickLinks";
import HomePromoCards from "./marketing/HomePromoCards";
import MobileAppBanner from "./marketing/MobileAppBanner";
import "./Home.css";

const HERO_BRAND_LOGO = `${process.env.PUBLIC_URL || ""}/images/ehgezly-hero-logo.png?v=4`;

const svcTranslations = {
  Barber: "حلاق",
  "Hair & Nail": "صالون شعر وأظافر",
  "Laser Clinics": "عيادات الليزر",
  Makeup: "مكياج",
  Massage: "مساج",
  "Pet Services": "خدمات الحيوانات الأليفة",
};

const serviceImages = {
  Barber: "/images/barbershop.jpg",
  "Hair & Nail": "/images/hair.jpg",
  "Laser Clinics": "/images/hair1.jpg",
  Makeup: "/images/makeup.jpg",
  Massage: "/images/massage.jpg",
  "Pet Services": "/images/pet.png",
};

const serviceBoxes = [
  { title: "Barber" },
  { title: "Hair & Nail" },
  { title: "Laser Clinics" },
  { title: "Makeup" },
  { title: "Massage" },
  { title: "Pet Services" },
];

const HOME_TO_NAV = {
  "Hair & Nail": "Hair & Nail",
  Barber: "Barber",
  "Laser Clinics": "Laser Clinics",
  Makeup: "Makeup",
  Massage: "Massage",
  "Pet Services": "Pet Services",
};

const buildServicePath = (title, maybeTown) => {
  const base = `/service/${title.toLowerCase().replace(/\s+/g, "-")}`;
  return maybeTown ? `${base}/town/${encodeURIComponent(maybeTown)}` : base;
};

function ServicePill({ to, img, label }) {
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setImageReady(true);
    }
  }, [img]);

  return (
    <Link to={to} className="service-pill">
      {img && (
        <div className="service-pill-photo">
          {!imageReady && !imageFailed && (
            <div className="service-pill-loading-buffer" aria-hidden="true" />
          )}
          <img
            ref={imgRef}
            src={img}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className={`service-pill-image${imageReady ? " is-loaded" : ""}`}
            onLoad={() => setImageReady(true)}
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      <div className="service-pill-label-wrapper">
        <span className="service-pill-label">{label}</span>
      </div>
    </Link>
  );
}

const heroI18n = {
  en: {
    brand: "Ehgezly",
    langBtn: "AR 🇪🇬",
    login: "Login",
    signUpBtn: "Sign up",
    heroSubtitle: "Discover & book your beauty professionals",
    searchPh: "Search services…",
    searchBtn: "Search",
  },
  ar: {
    brand: "احجزلي",
    langBtn: "EN 🇺🇸",
    login: "تسجيل الدخول",
    signUpBtn: "إنشاء حساب",
    heroSubtitle: "اكتشف واحجز خبراء الجمال بسهولة",
    searchPh: "ابحث عن الخدمات…",
    searchBtn: "ابحث",
  },
};

export default function Home() {
  const { lang, toggleLang } = useLanguage();
  const t = heroI18n[lang];
  const m = marketingI18n[lang];
  const { town } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [aiSupportOpen, setAiSupportOpen] = useState(false);
  const WA_LINK =
    "https://api.whatsapp.com/send/?phone=+447748628476&text&type=phone_number&app_absent=0";
  const heroServicesRef = useRef(null);

  useScrollAnimate([lang]);

  const onSearch = (e) => {
    e.preventDefault();
    heroServicesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filteredServices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return serviceBoxes.filter((s) => {
      const en = s.title.trim().toLowerCase();
      const ar = (svcTranslations[s.title.trim()] || "").toLowerCase();
      return en.includes(q) || ar.includes(q);
    });
  }, [searchQuery]);

  return (
    <div
      className={`home-scene ${lang === "ar" ? "rtl" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
      lang={lang}
    >
      <MobileAppBanner t={m} lang={lang} />
      <div className="hero-container">
        <div className="hero-atmosphere" aria-hidden="true">
          <div className="hero-orb hero-orb--1" />
          <div className="hero-orb hero-orb--2" />
          <div className="hero-orb hero-orb--3" />
          <div className="hero-shimmer" />
          <div className="hero-grid-fine" />
        </div>
        <section className="hero">
          <div className="hero-top-left">
            <Link to="/login" className="top-btn login-btn">
              <IoLogInOutline className="btn-icon" />
              {t.login}
            </Link>
          </div>
          <div className="hero-top-right">
            <button className="top-btn lang-btn" onClick={toggleLang} aria-label="Change language">
              {t.langBtn}
            </button>
            <Link to="/register" className="top-btn list-btn">
              <IoPersonAddOutline className="btn-icon" />
              {t.signUpBtn}
            </Link>
          </div>
          <div className="hero-content">
            <div className="hero-logo-container">
              <img
                src={HERO_BRAND_LOGO}
                alt="Ehgezly"
                className="hero-brand-logo"
                draggable="false"
              />
            </div>
            <h1 className="hero-headline" aria-label={t.heroSubtitle}>
              {t.heroSubtitle.split(/\s+/).filter(Boolean).map((word, i, words) => (
                <React.Fragment key={`${word}-${i}`}>
                  <span className="hero-headline-word" style={{ animationDelay: `${i * 0.1}s` }}>
                    {word}
                  </span>
                  {i < words.length - 1 ? " " : null}
                </React.Fragment>
              ))}
            </h1>
            <form className="search-card" onSubmit={onSearch}>
              <div className="search-field">
                <IoSearchOutline className="search-icon" aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder={t.searchPh}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label={t.searchPh}
                />
              </div>
              <button type="submit" className="search-button">
                {t.searchBtn}
              </button>
            </form>
          </div>
          <div ref={heroServicesRef} className="hero-services" aria-label="Quick categories">
          {filteredServices.map((service, i) => {
  const navTitle = HOME_TO_NAV[service.title] || service.title;
  const path = buildServicePath(navTitle, town);
              const img = serviceImages[service.title];
              const label =
                lang === "ar"
                  ? svcTranslations[service.title.trim()] || service.title
    : service.title;
  return (
    <ServicePill
      key={service.title + i}
      to={path}
      img={img}
      label={label}
    />
  );
})}
          </div>
        </section>
      </div>

      <HomeQuickLinks t={m} lang={lang} />
      <DownloadAppSection t={m} lang={lang} />
      <HomePromoCards t={m} lang={lang} />

      <div className={`wa-wrap${aiSupportOpen ? " wa-wrap-hide-when-ai-open" : ""}`} aria-live="polite">
        {waOpen && (
          <a
            className="wa-bubble"
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="wa-text">Need help? Chat with us on WhatsApp!</span>
          </a>
        )}
        <button
          type="button"
          className="wa-btn"
          aria-label={waOpen ? "Hide WhatsApp message" : "Chat on WhatsApp"}
          onClick={() => setWaOpen((v) => !v)}
        >
          <img
            src="/images/whatsapp.png"
            alt=""
            aria-hidden="true"
            width="64"
            height="64"
          />
        </button>
      </div>

      <AISupport onOpenChange={setAiSupportOpen} />
    </div>
  );
}
